import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { GroupedStaffList } from '@/types/settings-api';

/**
 * Canonical React Query key for the organisation's technician candidates.
 */
export function techniciansQueryKey(
  organizationId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'technicians', organizationId];
}

/** One staff member as the device form's Technician picker needs them. */
export interface TechnicianOption {
  /** `dbo.AddStaff.Id` (`staffSetupId` on the wire) — what `technicianId` takes. */
  id: string;
  /** "First Last"; falls back to the email, then the id, so no option is blank. */
  name: string;
}

/**
 * The tenant role whose members are offered as technicians (Andrew,
 * 2026-09-12: "technician should show only technician as role, not all
 * staff").
 *
 * This is the STABLE code the backend derives from
 * `dbo.TenantRoles.TenantMasterId` (Technician = master id 21) and returns as
 * `GroupedStaffList.roleCode` — PROD-2155. It is not the tenant's label, so
 * renaming the role no longer empties the picker.
 */
export const TECHNICIAN_ROLE_CODE = 'technician';

/**
 * The old match key: the tenant's own editable label for the role.
 *
 * FALLBACK ONLY, AND TEMPORARY. It exists so this app keeps working against a
 * backend that has not shipped `roleCode` yet — the two deploy independently
 * and the frontend goes out first. It carries the bug PROD-2155 is about: a
 * tenant that renames its Technician role disappears from a label match.
 *
 * REMOVE IT once PracticeManagement's PROD-2155 change is deployed to every
 * environment this app talks to (dev and prod; the backend's `master` deploys
 * to both). Deleting `isTechnicianLabel` and its use in `isTechnicianGroup` is
 * the whole removal — `TECHNICIAN_ROLE_CODE` and the tests that pin the code
 * path stay.
 */
export const TECHNICIAN_ROLE_NAME = 'technician';

function isTechnicianLabel(roleName: string | null | undefined): boolean {
  return (roleName ?? '').trim().toLowerCase() === TECHNICIAN_ROLE_NAME;
}

/**
 * Is this group the technicians?
 *
 * The code DECIDES whenever the server sent one, in both directions: a group
 * with a non-technician `roleCode` is excluded even if somebody has named it
 * "Technician", which is the other half of the label bug (a production audit
 * found roles renamed onto profession-sounding names). The label is consulted
 * only when the server sent no code at all — an older backend, or a role the
 * server could not classify.
 */
function isTechnicianGroup(group: GroupedStaffList | null | undefined): boolean {
  const code = group?.roleCode;
  if (code != null && code.trim() !== '') {
    return code.trim().toLowerCase() === TECHNICIAN_ROLE_CODE;
  }
  return isTechnicianLabel(group?.roleName);
}

/**
 * Pure: the grouped staff list → the Technician-role members as a flat,
 * de-duplicated, A→Z picker list. Only technician groups contribute (see
 * `isTechnicianGroup`); a dual-role person who is a technician appears once.
 */
export function flattenTechnicians(
  groups: GroupedStaffList[] | null | undefined,
): TechnicianOption[] {
  const out: TechnicianOption[] = [];
  const seen = new Set<string>();
  for (const group of groups ?? []) {
    if (!isTechnicianGroup(group)) continue;
    for (const s of group?.staffList ?? []) {
      const id = s.staffSetupId;
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const name = [s.firstName, s.lastName].filter(Boolean).join(' ').trim();
      out.push({ id, name: name || s.emailId || id });
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Active staff of the organisation holding the Technician role, as technician
 * candidates for a device — PROD-2008 (epic PROD-2007).
 *
 *   GET /api/Staff/StaffList?OrganizationId={org}&Archive=false
 *
 * The same endpoint the Staff tab reads (`use-staff-list.ts`), reduced to the
 * technician group and flattened. `StaffList` has no role or practice filter
 * of its own, so the role filter is client-side and the SERVER stays the
 * authority — `ResourceService.ResolveTechnician` rejects anyone who is not
 * an active staff member linked to the device's practice AND does not hold the
 * Technician master role (PROD-2155), and the form surfaces that message on
 * the field. This filter decides what is OFFERED; it is not the enforcement.
 */
export function useTechnicians(organizationId: string | null | undefined) {
  return useQuery<TechnicianOption[]>({
    queryKey: techniciansQueryKey(organizationId),
    queryFn: async ({ signal }) => {
      if (!organizationId) {
        throw new Error('useTechnicians called without organizationId');
      }
      const qs = new URLSearchParams({
        OrganizationId: organizationId,
        Archive: 'false',
      });
      const res = await get<GroupedStaffList[] | null>(
        `/api/Staff/StaffList?${qs.toString()}`,
        { signal },
      );
      return flattenTechnicians(res);
    },
    enabled: !!organizationId,
    staleTime: 5 * 60_000,
    // A failed picker load is shown as the Technician field's helper text
    // ("Technicians unavailable right now…"); the global "Something went
    // wrong" toast must not fire on top of it.
    meta: { skipGlobalError: true },
  });
}
